import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRotationStore } from '../../../../context/RotationContext';
import { FiltersSelectionWrapper, UpperWrapper, Wrapper, VoyageSelectionList, VoyageStatusMarker } from './VoyageSelectionPanel.styles';
import { useAppStore } from '../../../../context/AppContext';
import { VoyageOption } from '../../../../types/ViewModel/VoyageOption';
import { FilterButton, MediumButton, SortButton } from '../../../../styles/button.style';
import { PanelButton, PanelContent, PanelSecondaryHeader } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';
import { useStowageStore } from 'src/context/StowageContext';

type Props = {
    voyageOptionsList: VoyageOption[];
    onClearFiltersCallback?: (...args: any[]) => void;
    onSelectVoyageCallback?: (selectedVoyageNumbers: string[], selectedVoyageNumber: string, isVoyageNumberOn: boolean) => void;
};

const VoyageSelectionPanel: React.FC<Props> = ({ voyageOptionsList, onClearFiltersCallback, onSelectVoyageCallback }) => {
    const listRef = useRef<HTMLDivElement>(null);
    const [voyagesList, setVoyagesList] = useState<VoyageOption[]>([]);
    const [filteredVoyagesList, setFilteredVoyagesList] = useState<VoyageOption[]>([]);
    const [showAllVoyages, setShowAllVoyages] = useState<boolean>(false);
    const { isVoyageSelectionPanelOpen, setIsVoyageSelectionPanelOpen } = useAppStore();
    const { selectedStowageVoyages, setSelectedStowageVoyages } = useRotationStore();
    const [isSortedVoyageListDesc, setIsSortedVoyageListDesc] = useState<boolean>(false);
    const { mouseClickPosition, setMouseClickPosition } = useStowageStore();
    const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);

    useEffect(() => {
        if (!voyageOptionsList) return;
        if (voyageOptionsList.length > 0) {
            let isDescending = false;

            if (localStorage.getItem('isSortedVoyageListDesc')) {
                isDescending = localStorage.getItem('isSortedVoyageListDesc') === 'true';
            }

            const sortedList = sortVoyagesList(voyageOptionsList, isDescending);
            setVoyagesList(sortedList);
            setIsSortedVoyageListDesc(isDescending);
        } else {
            setVoyagesList([]);
        }
    }, [voyageOptionsList]);

    // Improved effect to scroll to appropriate position when the list changes or panel opens
    useEffect(() => {
        if (isVoyageSelectionPanelOpen && filteredVoyagesList.length > 0 && shouldAutoScroll) {
            scrollToPosition();
        }
    }, [isVoyageSelectionPanelOpen, filteredVoyagesList, isSortedVoyageListDesc, shouldAutoScroll]);

    useEffect(() => {
        if (!voyagesList) return;

        if (voyagesList.length > 0) {
            // Apply filtering based on showAllVoyages state
            if (showAllVoyages) {
                setFilteredVoyagesList(voyagesList);
            } else {
                // First find the most recent completed voyage (largest voyage number with "completed" status)
                const completedVoyages = voyagesList
                    .filter((voyage) => voyage.status === 'completed')
                    .sort((a, b) => parseInt(b.voyageNumber) - parseInt(a.voyageNumber));

                const lastCompletedVoyage = completedVoyages.length > 0 ? completedVoyages[0] : null;

                const filtered = voyagesList.filter((voyage) => {
                    // Include the voyage if:
                    return (
                        // It's the most recent completed voyage
                        (lastCompletedVoyage && voyage.voyageNumber === lastCompletedVoyage.voyageNumber) ||
                        // Or it's ongoing
                        voyage.status === 'commenced' ||
                        // Or it's scheduled/planned
                        voyage.status === 'scheduled'
                    );
                });

                setFilteredVoyagesList(filtered);
            }
        } else {
            setFilteredVoyagesList([]);
        }
    }, [voyagesList, showAllVoyages]);

    // Enhanced function to scroll to the appropriate position based on sort order
    const scrollToPosition = (): void => {
        if (!listRef.current) return;

        // Determine scroll position based on sort order
        const scrollPos = isSortedVoyageListDesc ? 0 : listRef.current.scrollHeight;

        // Immediate attempt
        listRef.current.scrollTop = scrollPos;

        // Additional attempt with minimal delay for reliability
        setTimeout(() => {
            if (listRef.current) {
                listRef.current.scrollTop = scrollPos;
            }
        }, 50);
    };

    const sortVoyagesList = (voyages: VoyageOption[], isDescending: boolean) => {
        if (!voyages || voyages.length === 0) return [];

        const sortedVoyages = [...voyages];
        sortedVoyages.sort((a, b) => {
            return isDescending ? (a.voyageNumber > b.voyageNumber ? -1 : 1) : a.voyageNumber > b.voyageNumber ? 1 : -1;
        });

        return sortedVoyages;
    };

    const toggleSortEventsList = () => {
        const newSortOrder = !isSortedVoyageListDesc;
        setIsSortedVoyageListDesc(newSortOrder);
        localStorage.setItem('isSortedVoyageListDesc', JSON.stringify(newSortOrder));

        // Apply sorting immediately
        const sortedList = sortVoyagesList(voyagesList, newSortOrder);
        setVoyagesList(sortedList);

        // Re-enable auto-scroll for sorting and explicitly scroll after sort
        setShouldAutoScroll(true);
        if (listRef.current) {
            const targetScrollPos = newSortOrder ? 0 : listRef.current.scrollHeight;
            listRef.current.scrollTop = targetScrollPos;
            // Disable auto-scroll again after manual positioning
            setTimeout(() => setShouldAutoScroll(false), 100);
        }
    };

    const toggleFilterPanel = (e) => {
        const isOpening = !isVoyageSelectionPanelOpen;
        setIsVoyageSelectionPanelOpen(isOpening);

        // If opening the panel, enable auto-scroll - let the useEffect handle the scrolling
        if (isOpening) {
            setShouldAutoScroll(true);
        }
        setMouseClickPosition({
            top: e.clientY,
            left: e.clientX,
        });
    };

    const onClearFilters = () => {
        setIsVoyageSelectionPanelOpen(false);
        setSelectedStowageVoyages([]);
        if (onClearFiltersCallback) {
            onClearFiltersCallback();
        }
    };

    const onSelectVoyage = (selectedVoyageNumber: string) => {
        let _selectedVoyages = selectedStowageVoyages ? [...selectedStowageVoyages] : [];

        const index = _selectedVoyages.indexOf(selectedVoyageNumber);
        if (index === -1) {
            _selectedVoyages.push(selectedVoyageNumber);
        } else {
            _selectedVoyages.splice(index, 1);
        }

        _selectedVoyages = _selectedVoyages.sort();
        setSelectedStowageVoyages(_selectedVoyages);

        if (onSelectVoyageCallback) {
            onSelectVoyageCallback(_selectedVoyages, selectedVoyageNumber, index === -1);
        }
    };

    const onClosePanel = () => {
        setIsVoyageSelectionPanelOpen(false);
        setMouseClickPosition(null);
    };

    const createVoyageKey = (nr: string): string => {
        return 'v-' + nr;
    };

    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.MEDIUM_WIDTH,
        height: 350, // Estimate the panel height
    };

    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    const getVoyageSelectPanel = () => {
        if (isVoyageSelectionPanelOpen) {
            return (
                <OdfjellPanel
                    headerTitle={'Select voyages'}
                    defaultPosition="center"
                    initialPosition={mouseClickPosition ? panelPosition : undefined}
                    panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
                    onAction={onClosePanel}
                    onCancel={onClosePanel}
                    showCancelButton={false}
                    actionButtonText="OK"
                    leftButtons={
                        <PanelButton onClick={onClearFilters} title="Clear all voyage filters">
                            Reset selection
                        </PanelButton>
                    }
                >
                    <PanelSecondaryHeader alignItems="right">
                        <MediumButton
                            onClick={() => {
                                setShowAllVoyages(!showAllVoyages);
                                // Enable auto-scroll when toggling filter to position correctly
                                setShouldAutoScroll(true);
                            }}
                        >
                            {showAllVoyages ? 'Hide previous voyages' : 'Show previous voyages'}
                        </MediumButton>
                        <SortButton onClick={toggleSortEventsList}>
                            <FontAwesomeIcon
                                title={`Click to sort by date in ${!isSortedVoyageListDesc ? 'descending' : 'ascending'} order`}
                                icon={!isSortedVoyageListDesc ? 'sort-amount-down' : 'sort-amount-up'}
                            />
                        </SortButton>
                    </PanelSecondaryHeader>
                    <PanelContent>
                        <VoyageSelectionList>
                            {selectedStowageVoyages && filteredVoyagesList && filteredVoyagesList.length > 0 && (
                                <div
                                    ref={listRef}
                                    className="voyage-list"
                                    onScroll={() => {
                                        // Disable auto-scroll when user manually scrolls
                                        setShouldAutoScroll(false);
                                    }}
                                >
                                    <ul>
                                        {filteredVoyagesList.map((voyageOption) => {
                                            const voyageStatus = voyageOption.status; //getVoyageStatus(voyageOption.voyageNumber);
                                            let statusLabel = '';

                                            switch (voyageStatus) {
                                                case 'commenced':
                                                    statusLabel = 'Commenced';
                                                    break;
                                                case 'completed':
                                                    statusLabel = 'Completed';
                                                    break;
                                                case 'scheduled':
                                                    statusLabel = 'Scheduled';
                                                    break;
                                                case 'N/A':
                                                    statusLabel = '';
                                                    break;
                                                default:
                                                    statusLabel = 'Completed';
                                            }

                                            return (
                                                <li
                                                    key={createVoyageKey(voyageOption.voyageNumber)}
                                                    className={voyageOption.disabled ? 'disabled' : ''}
                                                    onClick={(e) => {
                                                        // Don't allow selection of disabled voyages
                                                        if (voyageOption.disabled) return;
                                                        // Prevent duplicate execution if the click was on the checkbox itself
                                                        if ((e.target as HTMLInputElement).type !== 'checkbox') {
                                                            onSelectVoyage(voyageOption.voyageNumber);
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedStowageVoyages?.includes(voyageOption.voyageNumber)}
                                                        onChange={() => {
                                                            if (!voyageOption.disabled) {
                                                                onSelectVoyage(voyageOption.voyageNumber);
                                                            }
                                                        }}
                                                        disabled={voyageOption.disabled}
                                                        id={`voyage-${voyageOption.voyageNumber}`}
                                                    />
                                                    <span>{voyageOption.voyageNumber}</span>

                                                    <span>
                                                        {voyageOption.firstPortCall} - {voyageOption.lastPortCall}
                                                    </span>
                                                    {voyageOption.status && voyageOption.status !== 'N/A' && (
                                                        <VoyageStatusMarker status={voyageOption.status}>
                                                            {voyageOption
                                                                ? voyageOption.status.charAt(0).toUpperCase() + voyageOption.status.slice(1)
                                                                : statusLabel}
                                                        </VoyageStatusMarker>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </VoyageSelectionList>
                    </PanelContent>
                </OdfjellPanel>
            );
        }
        return null;
    };

    return (
        <>
            <Wrapper>
                <UpperWrapper>
                    <FiltersSelectionWrapper>
                        <FilterButton isSelected={isVoyageSelectionPanelOpen} onClick={(e) => toggleFilterPanel(e)} title="Open voyage filter">
                            <FontAwesomeIcon icon="filter" />
                        </FilterButton>
                    </FiltersSelectionWrapper>
                </UpperWrapper>
            </Wrapper>
            {getVoyageSelectPanel()}
        </>
    );
};

export default VoyageSelectionPanel;
